<template>
  <div
    class="expiry tw:px-4 tw:py-1 tw:flex tw:items-center tw:justify-between"
  >
    <div class="tw:flex tw:items-center tw:gap-1 tw:text-xs tw:font-bold">
      <BaseIcon icon="clock" />
      {{
        getLocaleText("mention_stream_expiry_watcher.expires_in", { expiresIn })
      }}
    </div>
    <div class="tw:flex tw:gap-1">
      <BaseButton
        data-testid="extend-button"
        :label="getLocaleText('mention_stream_expiry_watcher.extend')"
        new-design
        size="sm"
        @click="extend"
      />
      <BaseButton
        data-testid="dismiss-button"
        :label="getLocaleText('mention_stream_expiry_watcher.dismiss')"
        new-design
        size="sm"
        @click="dismiss"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { addHours } from "date-fns/addHours";
import { computed, onBeforeUnmount, onMounted, ref } from "vue";

import { getLocaleText } from "shared/boot/i18n";
import BaseButton from "shared/components/base/BaseButton";
import BaseIcon from "shared/components/base/BaseIcon";
import { durationTimer } from "shared/helpers/date";
import { useStreamsStore } from "shared/stores/streams";
import type { Nullable, Stream } from "shared/types";

interface MentionStreamExpiryWatcherProps {
  stream: Stream;
}

const props = defineProps<MentionStreamExpiryWatcherProps>();

const { updateStream } = useStreamsStore();

const expiresIn = ref<string>("");

const expiryDate = computed<Nullable<Date>>(() =>
  props.stream.expires_at ? new Date(props.stream.expires_at) : null
);

let intervalId: Nullable<ReturnType<typeof setInterval>> = null;

function updateTimer(): void {
  if (!expiryDate.value) return;

  expiresIn.value = durationTimer(expiryDate.value, new Date());
}

function extend(): void {
  if (!props.stream.expires_at) return;

  updateStream({
    id: props.stream.id,
    params: {
      expires_at: addHours(new Date(props.stream.expires_at), 72).toISOString(),
    },
  });
}

function dismiss(): void {
  updateStream({
    id: props.stream.id,
    params: { expiration_dismissed: true },
  });
}

onMounted(() => {
  intervalId = setInterval(updateTimer, 1000);
  updateTimer();
});

onBeforeUnmount(() => {
  if (intervalId) clearInterval(intervalId);
});
</script>
